import React from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { useLoaderData } from "react-router";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { IUserProfileData } from "../SiteManagement/Users/types/users";

import "../../assets/css/wibb/pages/profile.scss";
import { alertActions } from "../../features/alert/alertSlice";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { getUser, updateUser } from "../../lib/api";
import { PHONE_REGEXP } from "../../lib/globals";
import { displayFormAlert, getUserFromLocalStorage } from "../../lib/utils";
import { IUser } from "../../types/api";

export const loader = async ({ params }: any) => {
    let user;

    if (params.userId !== undefined) {
        user = await getUser(params.userId);
    } else {
        const localUser = getUserFromLocalStorage();
        user = localUser && (await getUser(localUser.id));
    }

    return user;
};

const Profile = (): React.JSX.Element => {
    const { user } = useLoaderData() as { user: IUser };
    const dispatch = useAppDispatch();

    const profileFormSchema = yup.object().shape(
        {
            id: yup.number().required(),
            firstName: yup.string().required("First Name is required"),
            lastName: yup.string().required("Last Name is required"),
            email: yup
                .string()
                .required("Email is required")
                .email("Email is invalid"),
            password: yup.string().when("password", {
                is: "",
                then: schema =>
                    schema
                        .transform((value, originalValue) => {
                            if (!value) {
                                return null;
                            }
                            return originalValue;
                        })
                        .nullable()
                        .optional(),
                otherwise: schema =>
                    schema.min(
                        8,
                        "Password length should be at least 8 characters",
                    ),
            }),
            confirmPassword: yup
                .string()
                .when(["password", "confirmPassword"], {
                    is: "",
                    then: schema =>
                        schema
                            .transform((value, originalValue) => {
                                if (!value) {
                                    return null;
                                }
                                return originalValue;
                            })
                            .nullable()
                            .optional(),
                    otherwise: schema =>
                        schema
                            .min(
                                8,
                                "Password length should be at least 8 characters",
                            )
                            .oneOf(
                                [yup.ref("password")],
                                "Passwords do not match",
                            ),
                }),
            phone: yup
                .string()
                .required("Phone number is required")
                .matches(PHONE_REGEXP, "Phone number is not valid"),
            city: yup.string().required("City is required"),
            state: yup
                .string()
                .max(2)
                .required("State abbreviation is required"),
        },
        [
            ["password", "password"],
            ["confirmPassword", "password"],
            ["confirmPassword", "confirmPassword"],
        ],
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IUserProfileData>({
        mode: "onChange",
        resolver: yupResolver(profileFormSchema),
        defaultValues: {
            id: user.id,
            email: user.email,
            firstName: user.meta.firstName,
            lastName: user.meta.lastName,
            phone: user.meta.phoneNumbers.primary,
            password: "",
            confirmPassword: "",
            city: user.meta.city,
            state: user.meta.state,
        },
    });

    const onSubmit: SubmitHandler<IUserProfileData> = async data => {
        await updateUser(data)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Profile updated successfully",
                        dismissible: true,
                    }),
                );
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating your profile.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    const onError: SubmitErrorHandler<IUserProfileData> = (): void => {};

    return (
        <div data-testid="profileContent">
            <Container className="py-5 profile">
                <Row>
                    <Col>
                        <Form
                            onSubmit={handleSubmit(onSubmit, onError)}
                            id="profileForm"
                        >
                            <Row>
                                <Col>
                                    <h2>Edit Account</h2>
                                    <p>
                                        <b>Note:</b> All fields are required
                                    </p>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6}>
                                    <Row>
                                        <Col>
                                            <h4>Personal Info</h4>
                                        </Col>
                                    </Row>
                                    <Form.Group controlId="firstName" as={Row}>
                                        <Form.Label column sm={6}>
                                            First Name
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="text"
                                                {...register("firstName", {
                                                    required:
                                                        "First Name is required",
                                                })}
                                                aria-invalid={
                                                    errors.firstName
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.firstName &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.firstName
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group controlId="lastName" as={Row}>
                                        <Form.Label column sm={6}>
                                            Last Name
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="text"
                                                {...register("lastName", {
                                                    required:
                                                        "Last Name is required",
                                                })}
                                                aria-invalid={
                                                    errors.lastName
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.lastName &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.lastName
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Row>
                                        <Col>
                                            <h4>Contact Info</h4>
                                        </Col>
                                    </Row>
                                    <Form.Group controlId="email" as={Row}>
                                        <Form.Label column sm={6}>
                                            Email
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="email"
                                                {...register("email", {
                                                    required:
                                                        "Email address  is required",
                                                })}
                                                aria-invalid={
                                                    errors.email
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.email &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.email
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group controlId="phone" as={Row}>
                                        <Form.Label column sm={6}>
                                            Phone
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="text"
                                                {...register("phone")}
                                                aria-invalid={
                                                    errors.phone
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.phone &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.phone
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group controlId="city" as={Row}>
                                        <Form.Label column sm={6}>
                                            City
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="text"
                                                {...register("city")}
                                                aria-invalid={
                                                    errors.city
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.city &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.city
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group controlId="state" as={Row}>
                                        <Form.Label column sm={6}>
                                            State (abbrev.)
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="text"
                                                {...register("state")}
                                                aria-invalid={
                                                    errors.state
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.state &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.state
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <h4>Security Info</h4>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={8}>
                                    <Form.Group controlId="password" as={Row}>
                                        <Form.Label column sm={6}>
                                            Password
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="password"
                                                {...register("password")}
                                                aria-invalid={
                                                    errors.password
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.password &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.password
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                    <Form.Group
                                        controlId="confirmPassword"
                                        as={Row}
                                    >
                                        <Form.Label column sm={6}>
                                            Confirm Password
                                        </Form.Label>
                                        <Col sm={6}>
                                            <Form.Control
                                                type="password"
                                                {...register("confirmPassword")}
                                                aria-invalid={
                                                    errors.confirmPassword
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.confirmPassword &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.confirmPassword
                                                        .message as string,
                                                })}
                                        </Col>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button variant="primary" type="submit">
                                        Update
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Profile;
